<!-- ============================================================
     Free-listening limit reached — Subscribe / Cancel prompt
     ============================================================ -->
<div class="ap-limit-popup" *ngIf="limitPopupOpen">
  <div class="ap-limit-backdrop" (click)="onLimitCancel()"></div>

  <div class="ap-limit-box" role="dialog" aria-modal="true">
    <p class="ap-limit-msg">{{ 'AUDIO_PLAYER.LIMIT_MESSAGE' | translate }}</p>

    <div class="ap-limit-actions">
      <button type="button" class="ap-limit-btn ap-limit-btn--ghost" (click)="onLimitCancel()">
        {{ 'AUDIO_PLAYER.LIMIT_CANCEL' | translate }}
      </button>
      <button type="button" class="ap-limit-btn ap-limit-btn--primary" (click)="onLimitSubscribe()">
        {{ 'AUDIO_PLAYER.LIMIT_SUBSCRIBE' | translate }}
      </button>
    </div>
  </div>
</div>

<!-- ============================================================
     Persistent bottom audio player bar
     ============================================================ -->
<!--
  The whole strip is the track, so a click anywhere on it opens the track page.
  Controls are the exception — see onBarClick(); mousedown is watched too so a
  seek or volume drag that ends over the bar does not count as a click on it.
-->
<div class="ap-bar"
     *ngIf="isPlayerOpen && !hideBar"
     [class.ap-bar--link]="canOpenTrackPage()"
     (mousedown)="onBarPress($event)"
     (click)="onBarClick($event)">

  <!-- ── Corner close button — sits centered on bar's top edge ── -->
  <button class="ap-close-corner" (click)="closePlayer()" title="Close Player">&#x2715;</button>

  <!-- ── Seek bar — hidden for radio ── -->
  <div *ngIf="!isRadio"
       class="ap-progress"
       (mousedown)="onProgressMouseDown($event)"
       (touchstart)="onProgressTouchStart($event)"
       (mousemove)="onProgressHover($event)"
       (mouseenter)="onProgressMouseEnter()"
       (mouseleave)="onProgressMouseLeave()">
    <div class="ap-progress__fill" [style.width.%]="progressPct"></div>
    <div class="ap-progress__thumb" [style.left.%]="progressPct"></div>
    <div class="ap-progress__tooltip"
         *ngIf="showHoverTime"
         [style.left.%]="hoverPct">
      {{ formatTime(hoverTime) }}
    </div>
  </div>

  <!-- ── Time row — hidden for radio ── -->
  <div class="ap-time-row" *ngIf="!isRadio">
    <span class="ap-time-current">{{ formatTime(currentTime) }}</span>
    <span class="ap-time-total">{{ formatTime(duration) }}</span>
  </div>

  <!-- ── Main content row ── -->
  <div class="ap-content">

    <!-- ── LEFT: Track info ─────────────────────────────────── -->
    <div class="ap-track">
      <!-- Radio: prefer playlist_thumbnail (injected by radio.component.ts), fallback to track thumbnail -->
      <img
        class="ap-track__thumb"
        [src]="isRadio
          ? (currentTrack?.playlist_thumbnail || currentTrack?.thumbnail || 'assets/images/blank_new.png')
          : (currentTrack?.thumbnail || 'assets/images/blank_new.png')"
        [alt]="currentTrack?.title"
        onerror="this.src='assets/images/blank_new.png'"
      />
      <div class="ap-track__meta">

        <!-- ── RADIO info order ───────────────────────────────── -->
        <!-- Line 1 (bold): [Radio badge]  radio_name | artist    -->
        <!-- Line 2: ID                                           -->
        <!-- Line 3: Title (regular weight)                       -->
        <!-- Note: playlist_name & playlist_thumbnail are NOT in  -->
        <!-- track-level API data — radio_name is used instead.   -->
        <ng-container *ngIf="isRadio">
          <span class="ap-track__radio-line1">
            <span class="ap-track__radio-badge">Radio</span>
            <span class="ap-track__radio-info">
              <!-- playlist_name = show/series name injected by radio.component.ts -->
              {{ currentTrack?.playlist_name || currentTrack?.radio_name }}<ng-container *ngIf="currentTrack?.authorTitle"> | {{ currentTrack?.authorTitle }}</ng-container>
            </span>
          </span>
          <span class="ap-track__id" *ngIf="currentTrack?.id">ID - {{ currentTrack?.id }}</span>
          <span class="ap-track__title ap-track__title--light">{{ currentTrack?.title }}</span>
        </ng-container>

        <!-- ── NORMAL TRACK info order (unchanged) ─────────────── -->
        <!-- Line 1: Title                                          -->
        <!-- Line 2: ID                                            -->
        <!-- Line 3: artist | album                                -->
        <ng-container *ngIf="!isRadio">
          <!-- The click lives on the bar now; the title keeps the hover so the
               destination still has something to point at. -->
          <span class="ap-track__title"
                [class.ap-track__title--link]="canOpenTrackPage()"
                [title]="canOpenTrackPage() ? 'Open track page' : null">{{ currentTrack?.title }}</span>
          <span class="ap-track__id" *ngIf="currentTrack?.id">ID - {{ currentTrack?.id }}</span>
          <span class="ap-track__artist">
            {{ currentTrack?.authorTitle }}
            <ng-container *ngIf="currentTrack?.album_name"> | {{ currentTrack?.album_name }}</ng-container>
          </span>
        </ng-container>

      </div>
    </div>

    <!-- ── CENTER: Playback controls ────────────────────────── -->
    <div class="ap-controls">

      <!-- Repeat — hidden for radio -->
      <button *ngIf="!isRadio"
        class="ap-btn ap-btn--icon"
        (click)="cycleRepeat()"
        [title]="'Repeat: ' + repeatMode"
        [ngClass]="{ 'ap-btn--active': repeatMode !== 'off', 'ap-btn--inactive': repeatMode === 'off' }">
        <!-- The loop the track page draws, in the same stroke weight as the
             shuffle beside it. Repeat-one is that loop with the count inside, so
             the three states stay one mark; off keeps the bar's own way of
             showing a toggle at rest, which is to fade it. -->
        <svg *ngIf="repeatMode === 'all'" viewBox="0 0 24 24" width="20" height="20" fill="none"
          stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8 7.6h8a4.4 4.4 0 0 1 0 8.8H8a4.4 4.4 0 0 1 0-8.8Z"/>
          <path d="m13.8 13.3 3.1 3.1-3.1 3.1"/>
        </svg>
        <svg *ngIf="repeatMode === 'one'" viewBox="0 0 24 24" width="20" height="20" fill="none"
          stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8 7.6h8a4.4 4.4 0 0 1 0 8.8H8a4.4 4.4 0 0 1 0-8.8Z"/>
          <path d="m13.8 13.3 3.1 3.1-3.1 3.1"/>
          <text x="12" y="14.9" text-anchor="middle" font-size="8" font-weight="700" fill="currentColor"
            stroke="none">1</text>
        </svg>
        <svg *ngIf="repeatMode === 'off'" viewBox="0 0 24 24" width="20" height="20" fill="none"
          stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" opacity="0.4">
          <path d="M8 7.6h8a4.4 4.4 0 0 1 0 8.8H8a4.4 4.4 0 0 1 0-8.8Z"/>
          <path d="m13.8 13.3 3.1 3.1-3.1 3.1"/>
        </svg>
      </button>

      <!-- Previous — hidden for radio -->
      <button *ngIf="!isRadio"
        class="ap-btn ap-btn--icon"
        (click)="prevTrack()"
        title="Previous">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
          <path d="M6 6h2v12H6zm3.5 6 8.5 6V6z"/>
        </svg>
      </button>

      <!-- 10s Backward — hidden for radio -->
      <button *ngIf="!isRadio"
        class="ap-btn ap-btn--icon"
        (click)="skipBackward()"
        title="Back 10s">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
          <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
          <text x="8.5" y="15.5" font-size="5.5" font-weight="bold" fill="currentColor">10</text>
        </svg>
      </button>

      <!-- Play / Pause — always visible -->
      <button class="ap-btn ap-btn--play" (click)="togglePlayPause()" [title]="isPlaying ? 'Pause' : 'Play'">
        <span *ngIf="isLoading" class="ap-spinner"></span>
        <svg *ngIf="!isLoading && !isPlaying" viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
          <path d="M8 5v14l11-7z"/>
        </svg>
        <svg *ngIf="!isLoading && isPlaying" viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
        </svg>
      </button>

      <!-- 10s Forward — hidden for radio -->
      <button *ngIf="!isRadio"
        class="ap-btn ap-btn--icon"
        (click)="skipForward()"
        title="Forward 10s">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
          <path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/>
          <text x="8.5" y="15.5" font-size="5.5" font-weight="bold" fill="currentColor">10</text>
        </svg>
      </button>

      <!-- Next — hidden for radio -->
      <button *ngIf="!isRadio"
        class="ap-btn ap-btn--icon"
        (click)="nextTrack()"
        title="Next">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
          <path d="M6 18l8.5-6L6 6v12zm2-8.14 5.14 2.14L8 14.14V9.86zM16 6h2v12h-2z"/>
        </svg>
      </button>

      <!-- Shuffle — hidden for radio -->
      <button *ngIf="!isRadio"
        class="ap-btn ap-btn--icon"
        (click)="toggleShuffle()"
        [title]="isShuffle ? 'Shuffle On' : 'Shuffle Off'"
        [ngClass]="{ 'ap-btn--active': isShuffle, 'ap-btn--inactive': !isShuffle }">
        <!-- Two lanes that swap: each runs in flat from the left, bends across the
             other and leaves through its own arrow. Same mark the track page
             draws, a shade larger here because a stroke reads lighter than the
             filled icons beside it. -->
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M2.8 7h3.6c2.9 0 4.3 2.3 5.3 5s2.4 5 5.3 5h3.2"/>
          <path d="M2.8 17h3.6c2.9 0 4.3-2.3 5.3-5s2.4-5 5.3-5h3.2"/>
          <path d="m17.6 4.4 2.9 2.6-2.9 2.6"/>
          <path d="m17.6 14.4 2.9 2.6-2.9 2.6"/>
        </svg>
      </button>

      <!-- Playback Speed Dropdown — hidden for radio -->
      <!-- [ngModel]+[ngValue]: required in Angular 7 — plain [value]+[selected] doesn't
           reliably update the displayed selection when cdr.detectChanges() re-runs bindings. -->
      <select *ngIf="!isRadio"
        class="ap-speed-select"
        [ngModel]="playbackRate"
        (ngModelChange)="onSpeedChange($event)"
        title="Playback Speed">
        <option *ngFor="let s of speedOptions" [ngValue]="s">{{ s }}x</option>
      </select>

    </div>

    <!-- ── RIGHT: Playlist / Share / Volume — all always visible ── -->
    <div class="ap-actions">

      <!-- Add to Playlist — always visible -->
      <button class="ap-btn ap-btn--icon" (click)="addToPlaylist()" title="Add to Playlist">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M14 10H2v2h12v-2zm0-4H2v2h12V6zm4 8v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM2 16h8v-2H2v2z"/>
        </svg>
      </button>

      <!-- Share — always visible -->
      <button class="ap-btn ap-btn--icon" (click)="shareTrack()" title="Share">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
        </svg>
      </button>

      <!-- Volume toggle — always visible -->
      <button class="ap-btn ap-btn--icon" (click)="toggleMute()" [title]="isMuted ? 'Unmute' : 'Mute'">
        <svg *ngIf="!isMuted" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
        </svg>
        <svg *ngIf="isMuted" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z"/>
        </svg>
      </button>

      <!-- Volume slider — always visible -->
      <input
        class="ap-volume"
        type="range"
        min="0"
        max="1"
        step="0.05"
        [value]="volume"
        (input)="onVolumeChange($event)"
        title="Volume"
        [style.background]="'linear-gradient(to right, #ea4e2a ' + (volume * 100) + '%, #162233 ' + (volume * 100) + '%)'"
      />

    </div>
  </div>
</div>
